<template>
  <BaseLayout>
    <div :class="{ 'marketplace-front': loginFront }">
      <div class="header-bar">
        <!--
          One navbar per page, never two. The home route renders the lean
          `TenantNavbar`, which reads the brand name from the public
          `GET /api/v1/current_tenant` endpoint; every other route keeps `MainMenu`,
          which carries the navigation links, the language switcher and the mobile
          drawer that the home page has no room for.

          The marketplace door replaces both with the minimal header: its body is
          empty and its chrome is the brand name, so a navbar with links would
          contradict it.
        -->
        <MarketplaceHeader v-if="loginFront" />
        <TenantNavbar v-else-if="isHome" />
        <MainMenu v-else />
      </div>

      <section v-if="showTenantBookingWidget" class="tenant-booking-rail">
        <AppointmentBookingWidget />
      </section>

      <div v-if="!hidePageContent" id="main-content" class="container mt-0" tabindex="-1">
        <router-view />
      </div>

      <SiteFooter v-if="!isHome && !loginFront" />

      <ServiceSeoDirector />
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import BaseLayout from '../BaseLayout.vue';
import MainMenu from './MainMenu.vue';
import TenantNavbar from '@/components/website/TenantNavbar.vue';
import MarketplaceHeader from '@/components/website/MarketplaceHeader.vue';
import SiteFooter from '@/components/website/SiteFooter.vue';
import ServiceSeoDirector from '@/components/seo/ServiceSeoDirector.vue';
import AppointmentBookingWidget from '@/components/scheduling/AppointmentBookingWidget.vue';
import { loadBranding } from '@/services/branding.service';

const route = useRoute();

const isHome = computed(
  () => route.name === 'home' || route.name === 'index' || route.name === 'index2',
);

// On the tenant home page AND /agendar the router-view page content is
// eradicated so the appointment widget is the single bookable section --
// no duplicate professional picker or competing content. Every other route
// keeps its page content untouched.
const hidePageContent = computed(() => isHome.value && isTenant.value);

// The marketplace door: the platform's public front is the login.
//
// It belongs to the apex only. A brand reached by path keeps its own front
// even when the apex is the door: the route table, the layout and the nav are
// shared between them, and only the history base differs.
const loginFront = computed(() => {
  const branding = loadBranding();
  return branding.frontMode === 'login' && !branding.pathPrefix;
});

// The star scheduling widget is the tenant's primary call to action, so it sits
// directly under the navbar on every tenant page, the home page included.
//
// The apex does not get it: the platform is a booking directory, and a tenant
// widget on it would offer a single clinic's agenda as if it were the platform's
// own.
//
// It used to be suppressed on the home route as well, because HomePage rendered
// its own copy and two of them would stack on the same screen. HomePage no longer
// renders it -- on the apex that page holds the brand directory, on a brand host it
// renders nothing -- so the exclusion had no reason left to exist and keeping it
// would have dropped the agenda off `/` entirely.
const isTenant = computed(() => !loadBranding().isPlatform);
const showTenantBookingWidget = computed(() => isTenant.value);
</script>

<style scoped>
/*
 * The marketplace door: the whole front is painted with the platform's own
 * palette, so the page a visitor lands on looks like the platform and not like
 * a stripped-down page. The gradient lives on the wrapper, so the header and
 * the login card share one background.
 */
.marketplace-front {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: radial-gradient(1100px 520px at 12% -8%, rgba(141, 23, 196, 0.55), transparent 62%),
    radial-gradient(900px 480px at 92% 6%, rgba(59, 130, 246, 0.3), transparent 58%),
    linear-gradient(160deg, #1a0035 0%, #2d0066 55%, #4a148c 100%);
  color: #f5f3ff;
}

/* The content column takes the remaining height, so the login sits in the
 * middle of the door instead of clinging to the header. */
.marketplace-front #main-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  overflow-x: hidden;
}

/* Same treatment as the home page section so the widget reads as one component
   whether it was reached from `/` or from any other tenant page. */
.tenant-booking-rail {
  background: linear-gradient(180deg, #f7f5fb 0%, #efe9f7 100%);
  padding: clamp(1.5rem, 3.5vw, 2.5rem) clamp(1rem, 5vw, 2rem);
  display: flex;
  justify-content: center;
}

@media (max-width: 720px) {
  .tenant-booking-rail {
    padding: 1.25rem 0.75rem;
  }
}
</style>
